import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import {
  Notification,
  NotificationType,
} from "@transfr-inc/dashboard-components";
import { Product } from "@transfr-inc/dashboard-components/constants";
import { useUnloadWarning } from "@transfr-inc/dashboard-components/hooks";

import { useOrganizationDetails } from "@hooks/queries/organization";
import { useApiRequest } from "@lib/hooks";
import { Role } from "@models/role";
import { useCurrentUser, useRole } from "@queries";
import { useContainer } from "@src/contexts/container-context";

import { ProfessionalLearningProduct } from "@components/custom/dropdowns/product-dropdown-list";

import {
  exportOrganizationProgramStats,
  getProgramOrganizationDailyStats,
  getProgramOrganizationStats as getProgramOrganizationGeneralStats,
  getStatsFileName,
} from "@services";
import { DashboardBanner, ProgramInsights } from "@views/dashboard/components";

import "./program.tab.scss";

export const ProgramTab = ({ professionalLearning = false }) => {
  const defaultProgramOption = { displayName: "All Programs" };
  const { classroomService, organizationService } = useContainer();

  const currentUser = useCurrentUser();
  const role = useRole();

  const { organizationCode, userId } = currentUser || {};

  const [dateRange, setDateRange] = useState({});
  const [programSelected, setProgramSelected] = useState(defaultProgramOption);
  const [classroomsSelected, setClassroomsSelected] = useState();
  const [generalStats, setGeneralStats] = useState({ isLoading: true });
  const [dailyStats, setDailyStats] = useState({ isLoading: true });
  const { orgCode = organizationCode } = useParams();
  const { data: organization } = useOrganizationDetails(orgCode);

  const { loading: downloadingFile, sendRequest: exportProgramData } =
    useApiRequest(() => {
      const classroomIds = classroomsSelected?.map((c) => c.id) ?? undefined;
      const filters = {
        dateRange,
        classroomIds,
        ...(professionalLearning && { only_professional_learning: true }),
      };
      const fileName = getStatsFileName(organization?.name, dateRange);
      const code = role === Role.INSTRUCTOR.id ? userId : organization?.code;

      return exportOrganizationProgramStats({
        organizationService,
        role,
        orgCodeOrUserId: code,
        filters,
        fileName,
        getMultiOrg: false,
      });
    }, false);

  useUnloadWarning({ trigger: downloadingFile });

  const getClassroomStats = (classroomId, filters) => {
    Promise.all([
      classroomService
        .getProgramClassroomStats(classroomId, filters)
        .then((response) => setGeneralStats({ isLoading: false, response })),
      classroomService
        .getProgramClassroomDailyStats(classroomId, filters)
        .then((response) => setDailyStats({ isLoading: false, response })),
    ]).catch(() => {
      setGeneralStats({ isLoading: false });
      setDailyStats({ isLoading: false });
    });
  };

  const getOrganizationStats = (orgCode, filters) => {
    Promise.all([
      getProgramOrganizationGeneralStats({
        organizationService,
        userId,
        role,
        orgCode,
        filters,
      }).then((response) => setGeneralStats({ isLoading: false, response })),
      getProgramOrganizationDailyStats({
        organizationService,
        userId,
        role,
        orgCode,
        filters,
      }).then((response) =>
        setDailyStats({ isLoading: false, response: response })
      ),
    ]).catch(() => {
      setGeneralStats({ isLoading: false });
      setDailyStats({ isLoading: false });
    });
  };

  useEffect(() => {
    const { startDate, endDate } = dateRange;
    const programId = programSelected?.programId ?? undefined;
    const classroomIds = classroomsSelected?.map((c) => c.id) ?? undefined;
    const filters = {
      dateRange,
      programId,
      classroomIds,
      ...(professionalLearning && { only_professional_learning: true }),
    };
    if (startDate && endDate) {
      getOrganizationStats(organization?.code, filters);
    }
  }, [dateRange, classroomsSelected, programSelected]);

  const onApplyFilters = ({ dateRange, classrooms, program }) => {
    const { startDate, endDate } = dateRange || {};
    clearStats();
    setDateRange({ startDate, endDate });
    setClassroomsSelected(classrooms);
    setProgramSelected(program);
  };

  const clearStats = () => {
    setDailyStats({ isLoading: true });
    setGeneralStats({ isLoading: true });
  };

  const onExportStatsData = () => {
    const { startDate, endDate } = dateRange;
    if (startDate && endDate) {
      exportProgramData();
    }
  };

  return (
    <div className="program-single-tab">
      <DashboardBanner
        description={
          professionalLearning
            ? ProfessionalLearningProduct.description
            : undefined
        }
        product={Product.PROG}
      />
      <ProgramInsights
        userInfo={{ userId, role }}
        orgCode={organization?.code}
        classroomFilterEnabled
        programFilterEnabled
        generalStats={generalStats?.response}
        dailyStats={dailyStats}
        onApplyFilters={onApplyFilters}
        classroom={classroomsSelected}
        onExportData={onExportStatsData}
        exportDataDisabled={downloadingFile}
        professionalLearning={professionalLearning}
      />
      <div className="notification-loader-container">
        {downloadingFile && (
          <Notification
            type={NotificationType.info}
            closable
            icon={["fa-solid", "spinner"]}
            iconConfig={{ spin: true }}
          >
            We’re preparing a download of{" "}
            <strong>{`${organization?.name}'s`}</strong> insights. Please
            wait...
          </Notification>
        )}
      </div>
    </div>
  );
};
